import { create } from 'zustand';

/**
 * YEP-803 / YEP-805 — the two things the unread dots need the client to remember, and nothing else.
 *
 * <p><b>Both verdicts are the server's now.</b> Chat unread comes from `digital_staff_conversations`
 * (`last_agent_message_at` vs `last_read_at`), task unread from `agent_tasks.read_key` — the state
 * the merchant last saw, so a task read while running and later failed is unread again. Neither
 * lives here.
 *
 * <p>What is left is the gap between an action and the server agreeing with it, which is why both
 * slices are IN-MEMORY ONLY (no `persist`): they answer "what has this tab just done", and a stale
 * one restored from storage would suppress a dot that has since become real again. That was the
 * whole failure of the previous version — a persisted client registry that `authStore.logout`
 * cleared and re-seeded on every login, hiding everything that happened while the merchant was
 * signed out.
 */

/** The parts of a task the optimistic read needs. Structural, so list and detail rows both fit. */
export interface TaskLike {
  id: string;
  status: string;
  retryCount?: number | null;
  endedAt?: string | null;
}

/** Is the task simply working? Queued and running are one thing — see `taskUnreadKey`. */
function isActive(status: string): boolean {
  return status === 'QUEUED' || status === 'RUNNING' || status === 'PROVISIONAL';
}

/**
 * The state a task was read at.
 *
 * <p>Used only to decide when a local "I just read this" stops applying, so the VALUE never has to
 * match chatbot-api's `read_key` — but every field the server discriminates on has to be in here,
 * or the note outlives the change it was meant to cover and suppresses a dot the server is
 * correctly reporting.
 *
 * <p>That is why `endedAt` is here. `AWAITING_APPROVAL` is not terminal, so the projector reuses
 * that row for the next turn: the merchant asks for a revision, a new draft is produced, and status
 * and retryCount both stay exactly as they were — `ended_at` is the only thing that moves. Without
 * it a revision on a pending draft would be swallowed in this tab until a reload, and pending
 * drafts are the most common state there is.
 *
 * <p>Mirrors `AgentTaskReadState.KEY_SQL` in chatbot-api. The two live in different repositories and
 * nothing can enforce the pairing, so a new discriminator there needs one here in the same change.
 */
export function taskUnreadKey(task: TaskLike): string {
  if (isActive(task.status)) return 'ACTIVE';
  return `${task.status}:${task.retryCount ?? 0}:${task.endedAt ?? ''}`;
}

/** The conversation the merchant is looking at right now — never unread, whatever the poll says. */
export interface OnScreenChat {
  conversationId: string;
  agentType: string;
}

interface UnreadState {
  /**
   * The conversation on screen, so the sidebar's 30s poll cannot relight a dot in front of a
   * merchant who is reading that very chat. Deliberately a single slot rather than a set of
   * "already reported" ids — a history would keep subtracting a conversation the agent posted in
   * again days later, and that dot would never come back.
   */
  onScreenChat: OnScreenChat | null;
  /**
   * taskId → the key it was read at, for the poll cycle between expanding a row and the server
   * agreeing. Self-expiring: the note is never cleared explicitly — the key stops matching the
   * moment the task reaches a new outcome (see taskUnreadKey/isTaskUnread), so a stale note can
   * never suppress a genuinely-new unread.
   */
  taskReadLocally: Record<string, string>;
  setOnScreenChat: (chat: OnScreenChat | null) => void;
  /** Unpark, but only if this is still the parked one — see the implementation. */
  clearOnScreenChat: (conversationId: string) => void;
  markTaskReadLocally: (taskId: string, key: string) => void;
  /** Kept for tests/manual reset; the poll no longer calls this — the note self-expires by key. */
  clearLocalTaskRead: (taskId: string) => void;
  /**
   * conversationId → { at, agentType } for a chat this tab has just read. The optimistic twin of
   * `onScreenChat`: that slot only silences the ONE conversation still on screen, so a chat the
   * merchant opened and then navigated away from would keep its row/section AGGREGATE dot lit for up
   * to a 30s poll. This lets `useAgentAttention` discount it immediately. Time-boxed by the reader
   * (`CHAT_READ_TTL_MS`) and dropped as soon as the summary agrees, so a fresh agent message is
   * never masked for more than that window.
   */
  chatReadLocally: Record<string, { at: number; agentType: string }>;
  markChatReadLocally: (conversationId: string, agentType: string) => void;
  clearLocalChatRead: (conversationId: string) => void;
}

export const useUnreadStore = create<UnreadState>()((set, get) => ({
  onScreenChat: null,
  taskReadLocally: {},
  chatReadLocally: {},
  // Skips `set` ENTIRELY when nothing changed, rather than setting an empty patch. The chat
  // sidebars re-assert this on every effect run, and zustand notifies every subscriber for any set
  // at all — with a component whose effect deps churn that is an unbounded render loop, not just a
  // slow path.
  setOnScreenChat: chat => {
    const current = get().onScreenChat;
    if (current === chat) return;
    if (
      current &&
      chat &&
      current.conversationId === chat.conversationId &&
      current.agentType === chat.agentType
    ) {
      return;
    }
    if (!current && !chat) return;
    set({ onScreenChat: chat });
  },
  // Unparks ONLY if the id given is still the parked one. An effect's cleanup runs alongside the
  // next effect's setup, and two reporters can hand over across a route change — an unconditional
  // null lets the OUTGOING conversation's cleanup erase the INCOMING one that was just parked, and
  // then the poll relights a dot for the chat the merchant is looking at. Order-independent by
  // construction, rather than by reasoning about which effect React runs first.
  clearOnScreenChat: conversationId => {
    const current = get().onScreenChat;
    if (!current || current.conversationId !== conversationId) return;
    set({ onScreenChat: null });
  },
  markTaskReadLocally: (taskId, key) =>
    set(state =>
      state.taskReadLocally[taskId] === key
        ? {}
        : { taskReadLocally: { ...state.taskReadLocally, [taskId]: key } }
    ),
  clearLocalTaskRead: taskId =>
    set(state => {
      if (state.taskReadLocally[taskId] === undefined) return {};
      const next = { ...state.taskReadLocally };
      delete next[taskId];
      return { taskReadLocally: next };
    }),
  // A NEW timestamp every time, so re-reading a chat during a long visit keeps the discount alive.
  markChatReadLocally: (conversationId, agentType) =>
    set(state => ({
      chatReadLocally: {
        ...state.chatReadLocally,
        [conversationId]: { at: Date.now(), agentType },
      },
    })),
  clearLocalChatRead: conversationId =>
    set(state => {
      if (state.chatReadLocally[conversationId] === undefined) return {};
      const next = { ...state.chatReadLocally };
      delete next[conversationId];
      return { chatReadLocally: next };
    }),
}));

/**
 * Is this task unread — the server's verdict, unless this tab has just read it.
 *
 * <p>The local note is keyed by the state it was read at, so it stops applying the moment the task
 * reaches a new outcome: a row read while failed and then retried into a second failure lights up
 * again without waiting for anything to be cleared.
 */
export function isTaskUnread(
  taskReadLocally: Record<string, string>,
  task: TaskLike & { unread?: boolean }
): boolean {
  if (task.unread !== true) return false;
  return taskReadLocally[task.id] !== taskUnreadKey(task);
}

/** Is this conversation unread — the server says so, and it is not the one on screen. */
export function isChatUnread(
  onScreenChat: OnScreenChat | null,
  conversation: { conversationId: string; unread?: boolean }
): boolean {
  if (conversation.unread !== true) return false;
  return onScreenChat?.conversationId !== conversation.conversationId;
}
